import { BaseListViewStore } from 'core/stores';
import {
	notificationError,
	TableFilterUtility,
	getErrorMessage,
	translate,
	checkForColumnWidth,
	isSuperAdminOrganization,
} from 'core/utils';
import { observable, action, reaction, makeObservable } from 'mobx';
import { ModalParams, ConfirmModalParams } from 'core/models';
import { SyncStore } from 'modules/sync/stores';
import { ChannelStore } from 'modules/channel/stores';
import { checkPermissions, DateTimeCellData, BooleanToggleCellData, StatusCellData } from 'core/stores';
import { LookupStore } from 'modules/lookup/stores';
import { sourceOfChange } from 'common/constants';
import { localStorageHandler } from 'core/services';
import _ from 'lodash';
import { IconCellData } from 'core/stores/CellDataStore';

class ChannelListViewStore extends BaseListViewStore {
	@observable change = false;
	isSuperAdmin = false;
	syncStore = null;
	showActions = true;
	localStorageWidthKey = 'channels-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ChannelStore(rootStore),
			queryParams: {
				embed: ['seller', 'channelType', 'productInventoryRestockDate'],
			},
			findFunction: 'findChannel',
			tableData: {
				columns: [
					{
						key: 'name',
						title: 'CHANNEL.NAME',
						width: false,
						sortable: true,
						dataCell: IconCellData,
						onClick: channel => this.routes.edit(channel.id),
						selected: true,
					},
					{
						key: 'channelType.name',
						width: false,
						sortable: true,
						title: 'CHANNEL.CHANNELTYPE',
						selected: true,
					},
					{
						key: 'lastSyncRealTime',
						title: 'CHANNEL.LASTUPDATETIME',
						width: false,
						dataCell: DateTimeCellData,
						sortable: true,
						dateFormat: 'LLL',
						useLocalTime: true,
						selected: true,
					},
					{
						key: 'seller.applicationTitle',
						width: false,
						sortable: true,
						width: 200,
						title: 'CHANNEL.SELLER',
						selected: true,
						sellerOnly: true,
						enabled: isSuperAdminOrganization(rootStore),
					},
					{
						key: 'shouldSync',
						title: 'CHANNEL.SHOULDSYNC',
						width: false,
						dataCell: BooleanToggleCellData,
						sortable: false,
						onClick: (channel, field) => this.shouldSyncToggle(channel, field),
						selected: true,
					},
					{
						key: 'updateInventory',
						title: 'CHANNEL.UPLOADINVENTORY',
						width: false,
						dataCell: BooleanToggleCellData,
						sortable: false,
						onClick: (channel, field) => this.updateChannelInventory(channel, field),
						selected: true,
					},
					{
						key: 'lastSyncError',
						width: false,
						sortable: false,
						dataCell: StatusCellData,
						width: 100,
						title: 'CHANNEL.STATUS',
						selected: true,
					},
				],
				sortObject: [
					{
						field: 'name',
						dir: 'asc',
					},
				],
				actions: {
					onEdit: async item => this.routes.edit(item.id),
					onSync: async item => this.openSyncConfirmModal(item),
					onDelete: async item => this.openDeleteConfirmModal(item),
					onEditChannelWarehouseCodes: async item => this.openEditChannelWarehouseCodesModal(item),
					onEditChannelCustomLabel: async item => this.openCustomLabelModal(item),
				},
			},
			permissions: {
				update: () => this.authStore.hasPermission('channelSection.update'),
				create: () => this.authStore.hasPermission('channelSection.create'),
				remove: () => this.authStore.hasPermission('channelSection.remove'),
				read: () => this.authStore.hasPermission('channelSection.read'),
				sync: () => this.authStore.hasPermission('channelSection.create'),
				full: () => this.authStore.hasPermission('channelSection.full'),
			},
			routes: {
				detail: id => this.rootStore.routerStore.goTo('master.main.channel.preview', { id }),
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.channel.edit', {
						id,
					}),
				create: () => this.rootStore.routerStore.goTo('master.app.main.channel.add', {}),
			},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(this.rootStore)) {
			this.isSuperAdmin = true;
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findChannel);
		this.showActions = checkPermissions(this).actionsAllowed;
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			service: this.listStore.service,
		});
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.setButtons();
		this.setSearchVisible();
		this.setHeaderModes();
		reaction(
			() => this.rootStore.routerStore.routerState,
			state => {
				if (state.routeName !== 'master.app.main.channel.list') {
					return;
				}
			}
		);
	}

	@action.bound
	reloadChannels = () => {
		this.queryUtility.initialize(false);
	};

	@action.bound
	setAsChanged() {
		this.change = !this.change;
	}

	@action.bound
	setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					authorization: 'channelSection.create',
					text: 'EDITCHANNELNEW',
				},
			],
		});
	}

	@action.bound
	findChannel = async () => {
		this.suspend();
		this.queryUtility.setFilterParams(this, null, this.tableFilter);
		await this.listStore.findChannel(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.setButtons();
		this.resume();
	};

	@action.bound
	syncChannelOrders = async channel => {
		this.suspend();
		try {
			this.syncStore = new SyncStore(this.rootStore);
			const {
				data: { total, created, updated, canceled },
			} = await this.syncStore.syncByChannelAsync(
				channel.id,
				sourceOfChange.syncSingleChannelOrdersChannels + channel.name
			);
			if (created == 0) {
				this.rootStore.notificationStore.success([
					'CHANNELSYNCNONEWENTRIES',
					{ name: channel.name, updated, canceled },
				]);
			} else {
				this.rootStore.notificationStore.success([
					'CHANNELSYNCSUCCESS',
					{ name: channel.name, total, created, updated, failed: total - (updated + created), canceled },
				]);
			}
		} catch (err) {
			let message = getErrorMessage(err, { name: channel.name });
			const parsedMessage = translate(message);
			if (message[0] === parsedMessage) {
				message = ['CHANNELSYNCERROR.DEFAULTWITHORIGINAL', { name: channel.name, originalMessage: parsedMessage }];
			}
			this.rootStore.notificationStore.error(message);
		}
		this.rootStore.viewStore.getChannels();
		await this.queryUtility.initialize(false);
		this.resume();
	};

	@action.bound
	shouldSyncToggle = async (channel, field) => {
		this.suspend();
		try {
			channel[field] = !channel[field];
			await this.listStore.updateChannel(channel, sourceOfChange.updateChannelShouldSync);
			this.rootStore.notificationStore.success(['LOOKUPUPDATE.CHANNEL', { name: channel.name }]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	updateChannelInventory = async (channel, field) => {
		this.suspend();
		try {
			channel[field] = !channel[field];
			await this.listStore.updateChannel(channel, sourceOfChange.updatechannelInventory);
			this.rootStore.notificationStore.success(['LOOKUPUPDATE.CHANNEL', { name: channel.name }]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action
	deleteChannel = async channel => {
		this.suspend();
		try {
			await this.listStore.deleteChannel(channel);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.CHANNEL', { name: channel.name }]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			if (err.statusCode === 409 && err.data === 'MESSAGES.CHANNEL.ORDERSALREADYEXIST') {
				this.rootStore.notificationStore.error(err.data);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	@action.bound
	openDeleteConfirmModal = item => {
		this.selectedItem = item;
		this.deleteConfirmModal.open({
			message: ['LOOKUPDELETECONFIRM.CHANNEL', { name: item.name }],
		});
	};

	@action.bound
	openSyncConfirmModal = item => {
		this.selectedItem = item;
		this.syncConfirmModal.open({
			message: ['CHANNELSYNCCONFIRMMODAL', { name: item.name }],
		});
	};

	deleteConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.deleteChannel(this.selectedItem);
		},
	});

	syncConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.syncChannelOrders(this.selectedItem);
		},
	});

	openEditChannelWarehouseCodesModal = item => {
		this.selectedItem = item;
		this.editChannelWarehouseCodesModal.open();
	};

	closeEditChannelWarehouseCodesModal = () => {
		this.editChannelWarehouseCodesModal.close();
	};

	openCustomLabelModal = item => {
		this.selectedItem = item;
		this.customLabelModal.open();
	};

	closeCustomLabelModal = () => {
		this.customLabelModal.close();
	};

	editChannelWarehouseCodesModal = new ModalParams();
	customLabelModal = new ModalParams();
}

export default ChannelListViewStore;



// WEBPACK FOOTER //
// ./src/modules/channel/stores/ChannelListViewStore.js